import { LoginCredentials, LoginResponse, AuthUser, ChangePasswordData } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class AuthApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include', // Include cookies for refresh token
    };

    try {
      const response = await fetch(url, config);
      
      // Handle token expiration
      if (response.status === 401) {
        const errorData = await response.json();
        if (errorData.code === 'TOKEN_EXPIRED') {
          // Try to refresh token
          const refreshed = await this.refreshToken();
          if (refreshed.success) {
            // Retry original request with new token
            const newToken = localStorage.getItem('accessToken');
            if (newToken) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              };
              const retryResponse = await fetch(url, config);
              return await retryResponse.json();
            }
          } else {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
            throw new Error('Session expired');
          }
        } else {
          // For other 401 errors (like invalid credentials), throw the error data
          throw new Error(errorData.error || 'Authentication failed');
        }
      }

      // Check if response is ok for other status codes
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    console.log('AuthAPI: Making login request to:', `${API_BASE_URL}/auth/login`);
    console.log('AuthAPI: Login credentials:', { email: credentials.email, password: '***' });
    
    const result = await this.makeRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    console.log('AuthAPI: Login response received:', result);
    return result;
  }

  async logout(): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/auth/logout', {
      method: 'POST',
    });
  }

  async refreshToken(): Promise<{ success: boolean; data: { accessToken: string } }> {
    return this.makeRequest('/auth/refresh', {
      method: 'POST',
    });
  }

  async getProfile(): Promise<{ success: boolean; data: AuthUser }> {
    return this.makeRequest('/auth/me');
  }

  async updateProfile(data: { firstName: string; lastName: string }): Promise<{
    success: boolean;
    data: AuthUser;
    message: string;
  }> {
    return this.makeRequest('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: ChangePasswordData): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.makeRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkAuth(): Promise<{
    success: boolean;
    data: { authenticated: boolean; user: AuthUser };
  }> {
    return this.makeRequest('/auth/check');
  }
}

export const authApi = new AuthApiService();